import { useEffect, useLayoutEffect, useState, type RefObject } from "react";

// SSR-safe layout effect
const useIso = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Scroll-reveal state for an element. Defaults to visible (no flicker for
 * above-the-fold content, and nothing stays hidden if the observer never
 * fires); flips to hidden before first paint only when the element starts
 * below the viewport, then back to visible as it scrolls in.
 */
export function useRevealVisible(
  ref: RefObject<HTMLElement | null>,
  once = true
) {
  const [visible, setVisible] = useState(true);

  useIso(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const inView = rect.top < window.innerHeight - 60 && rect.bottom > 0;
    if (!inView) {
      setVisible(false);
    }
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    // Safety net: always reveal within 1.5s in case observer never fires.
    const safety = window.setTimeout(() => setVisible(true), 1500);
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return () => window.clearTimeout(safety);
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          window.clearTimeout(safety);
          setVisible(true);
          if (once) {
            io.disconnect();
          }
        } else if (!once) {
          setVisible(false);
        }
      },
      // Trigger a little before the element reaches the middle of the screen.
      { threshold: 0.05, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      window.clearTimeout(safety);
    };
  }, [ref, once]);

  return visible;
}
